import { cn } from "@/lib/utils"

/**
 * "Coming soon" label pill for expansion products (full-body MRI + its
 * add-ons, Ahead Membership) in markets at `checkup` status — the blood
 * check-up is bookable but these aren't open yet (see `isComingSoon` in
 * `i18n/config.ts`). Marks a nav item / card / hero so the waitlist-modal CTA
 * reads as "not yet" rather than a broken booking link.
 *
 * The label is passed in (already translated by the caller) so the component
 * stays i18n-agnostic and renders in both server and client trees.
 *
 * Text only — the status dot was dropped on 2026-07-29: it carried no meaning
 * the label didn't already state, and it read as a data marker next to the
 * biomarker chips on carousel cards.
 *
 * `variant`:
 *  - "light" (default) — the light-blue-100 / light-blue-900 pairing on
 *    white/light backgrounds (nav menus, cards). It used to be a faint neutral
 *    grey, which read as disabled chrome and was easy to skim past on the DE
 *    pricing page — the one market where these pills actually carry
 *    information. Blue is the colour "coming soon" already had on the imaging
 *    add-on badges and the "glass" variant, so this makes it one colour
 *    site-wide rather than introducing a new one.
 *  - "dark" — white text on a translucent dark pill, for photo / dark heroes.
 *  - "aubergine" — white text on a solid aubergine-800 pill, for busy photo
 *    backgrounds where the translucent "dark" pill washes out (carousel cards).
 *  - "glass" — frosted light-blue pill for photo surfaces (carousel cards, the
 *    photo hero): bright enough to read at a glance over any image, still
 *    translucent so it sits with the frosted biomarker chips rather than on
 *    top of them. Shares the light-blue-100 / light-blue-900 pairing the
 *    imaging add-on badges use, so "coming soon" is one colour site-wide.
 */
export function ComingSoonPill({
  label,
  className,
  variant = "light",
}: {
  label: string
  className?: string
  variant?: "light" | "dark" | "aubergine" | "glass"
}) {
  return (
    <span
      className={cn(
        // `self-start` keeps the pill at its content width when it's a direct
        // flex-column child (mobile test-detail hero) — otherwise the flex
        // default `align-items: stretch` stretches it full-width. AHD-10257.
        "type-label-sm inline-flex items-center self-start rounded-full px-2.5 py-1 font-medium whitespace-nowrap normal-case",
        variant === "dark"
          ? "bg-white/15 text-white backdrop-blur-[5px]"
          : variant === "aubergine"
            ? "bg-aubergine-800 text-white shadow-sm"
            : variant === "glass"
              ? "bg-light-blue-100/90 text-light-blue-900 backdrop-blur-[6px]"
              : "bg-light-blue-100 text-light-blue-900",
        className
      )}
    >
      {label}
    </span>
  )
}
